import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  Grid,
  useMediaQuery,
  useTheme,
  Divider,
  Tooltip,
  Alert,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select
} from '@mui/material';
import { useSnackbar } from 'notistack';
import facturaVentaService, {
  FacturaVenta,
  LibroIvaVentasSubtotalProvincia
} from '../../services/facturaVentaService';
import provinciaService, { Provincia } from '../../services/provinciaService';
import {
  agruparSubtotalesPorProvincia,
  claveProvincia,
  nombreProvinciaSubtotal,
  ordenarSubtotalesProvincia,
  provinciaEnFiltro,
  SIN_PROVINCIA_KEY,
  SIN_PROVINCIA_LABEL,
  sumarSubtotalesProvincia
} from '../../utils/libroIvaVentasProvincias';
import clienteService from '../../services/clienteService';
import { formatCurrency } from '../../utils/formatters';
import {
  Article as ArticleIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Summarize as SummarizeIcon,
  ArrowBack as ArrowBackIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  PriceCheck as PriceCheckIcon,
  Percent as PercentIcon,
  AttachMoney as AttachMoneyIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  BadgeOutlined as BadgeOutlinedIcon,
  SortByAlpha as SortByAlphaIcon,
} from '@mui/icons-material';
import KpisBar, { type KpiBarItem } from '../../components/shared/KpisBar';
import {
  LibroIvaResumenAccordion,
  LibroIvaResumenAgrupadoTable,
  libroIvaFooterCellSx as footerCellSx,
  type LibroIvaResumenGrupo
} from './components/LibroIvaResumenes';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import PDFViewer from '../../components/shared/PDFViewer';
import SearchableSelect from '../../components/SearchableSelect';
import facturaCompraService, { LibroIvaComprasItem } from '../../services/facturaCompraService';
import companiaService, { Compania } from '../../services/companiaService';
import { buildInformeIvaSimple } from '../../utils/ivaSimple';
import { generarPdfIvaSimple } from '../../utils/pdfIvaSimple';
import {
  fetchCompaniaParaPdf,
  fetchLogoPngDataUrl,
  lineasDatosCompania,
  pdfLibroIvaTableWidth,
  pdfLibroIvaVentasColumnStyles,
  pdfLibroIvaVentasResumenColumnStyles,
  PDF_LIBRO_IVA_VENTAS_RESUMEN_HEAD,
  PDF_LIBRO_IVA_VENTAS_PROVINCIA_HEAD,
  pdfLibroIvaVentasProvinciaColumnStyles,
  pdfHeadRowRightAligned,
  pdfFootLabelCell,
  pdfFootAmountCell,
  pdfLibroIvaFootStyles,
  PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS,
  type PdfFootCellDef
} from '../../utils/pdfLibroIvaCompania';
import { persistGlobalIdCompania, readGlobalIdCompania } from '../../utils/companiaPreferida';
import { useRegisterPageTour } from '../../components/tour/PageTourContext';
import { LIBRO_IVA_VENTAS_TOUR } from '../../config/tours/informesVentasTour';

// Función para formatear fecha en formato DD/MM/AAAA con ceros a la izquierda
const formatearFecha = (fecha: Date | string): string => {
  const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(date.getTime())) return '';
  
  const dia = date.getDate().toString().padStart(2, '0');
  const mes = (date.getMonth() + 1).toString().padStart(2, '0');
  const año = date.getFullYear();
  
  return `${dia}/${mes}/${año}`;
};

interface LibroIvaVentasRow {
  fechaEmision: string;
  tipoComprobante: string;
  puntoVenta: string; // Cambiado a string para mantener el formato con ceros
  numeroComprobante: string; // Cambiado a string para mantener el formato con ceros
  cliente: string;
  cuit: string;
  tipoIva: string;
  letra: string;
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  total: number;
  nroFormulario: number;
}

interface LibroIvaVentasTotales {
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  total: number;
}

const LIBRO_IVA_VENTAS_TABLE_COL_COUNT = 13;

const totalesVentasInicial: LibroIvaVentasTotales = {
  netoGravado: 0,
  iva21: 0,
  iva105: 0,
  iva27: 0,
  exento: 0,
  total: 0
};

const sumLibroIvaVentasRows = (rows: LibroIvaVentasRow[]): LibroIvaVentasTotales =>
  rows.reduce(
    (acc, row) => ({
      netoGravado: acc.netoGravado + row.netoGravado,
      iva21: acc.iva21 + row.iva21,
      iva105: acc.iva105 + row.iva105,
      iva27: acc.iva27 + row.iva27,
      exento: acc.exento + row.exento,
      total: acc.total + row.total
    }),
    { ...totalesVentasInicial }
  );

const buildVentasFootRowPdf = (totales: LibroIvaVentasTotales): PdfFootCellDef[] => [
  pdfFootLabelCell('Totales', 7),
  pdfFootAmountCell(formatCurrency(totales.netoGravado), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva21), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva105), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva27), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.exento), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.total), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS)
];

const TIPO_IVA_ORDEN_PDF = ['Resp. Insc.', 'Monotributo', 'Exento', 'Cons. Final', 'No Categorizado'];

const agruparTotalesPor = (
  rows: LibroIvaVentasRow[],
  keyOf: (row: LibroIvaVentasRow) => string
): LibroIvaResumenGrupo[] => {
  const grupos = new Map<string, LibroIvaResumenGrupo>();

  rows.forEach((row) => {
    const clave = keyOf(row) || '-';
    const prev = grupos.get(clave) ?? { clave, cantidad: 0, ...totalesVentasInicial };
    grupos.set(clave, {
      clave,
      cantidad: prev.cantidad + 1,
      netoGravado: prev.netoGravado + row.netoGravado,
      iva21: prev.iva21 + row.iva21,
      iva105: prev.iva105 + row.iva105,
      iva27: prev.iva27 + row.iva27,
      exento: prev.exento + row.exento,
      total: prev.total + row.total
    });
  });

  return Array.from(grupos.values());
};

const ordenarTiposIvaPdf = (a: string, b: string): number => {
  const indexA = TIPO_IVA_ORDEN_PDF.indexOf(a);
  const indexB = TIPO_IVA_ORDEN_PDF.indexOf(b);
  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;
  if (a === '-') return 1;
  if (b === '-') return -1;
  return a.localeCompare(b, 'es');
};

const totalesToResumenRow = (label: string, totales: LibroIvaVentasTotales): string[] => [
  label,
  formatCurrency(totales.netoGravado),
  formatCurrency(totales.iva21),
  formatCurrency(totales.iva105),
  formatCurrency(totales.iva27),
  formatCurrency(totales.exento),
  formatCurrency(totales.total)
];

const resumenPorTipoIva = (rows: LibroIvaVentasRow[]): LibroIvaResumenGrupo[] =>
  agruparTotalesPor(rows, (row) => row.tipoIva).sort((a, b) => ordenarTiposIvaPdf(a.clave, b.clave));

const resumenPorLetra = (rows: LibroIvaVentasRow[]): LibroIvaResumenGrupo[] =>
  agruparTotalesPor(rows, (row) => row.letra).sort((a, b) => {
    if (a.clave === '-') return 1;
    if (b.clave === '-') return -1;
    return a.clave.localeCompare(b.clave, 'es');
  });

const buildVentasResumenPorTipoIvaBody = (rows: LibroIvaVentasRow[]): string[][] =>
  resumenPorTipoIva(rows).map((g) => totalesToResumenRow(g.clave, g));

const buildVentasResumenPorLetraBody = (rows: LibroIvaVentasRow[]): string[][] =>
  resumenPorLetra(rows).map((g) => totalesToResumenRow(g.clave, g));

const buildVentasProvinciaBody = (subtotales: LibroIvaVentasSubtotalProvincia[]): string[][] =>
  subtotales.map((s) => [
    nombreProvinciaSubtotal(s),
    String(s.cantidadComprobantes ?? 0),
    formatCurrency(s.totalImporteSinIva ?? 0),
    formatCurrency(s.totalIvainscripto ?? 0),
    formatCurrency(s.totalIvanoInscripto ?? 0),
    formatCurrency(s.totalIvaotro ?? 0),
    formatCurrency(s.totalExento ?? 0),
    formatCurrency(s.totalImporteTotal ?? 0)
  ]);

const buildVentasProvinciaFootRowPdf = (subtotales: LibroIvaVentasSubtotalProvincia[]): PdfFootCellDef[] => {
  const t = sumarSubtotalesProvincia(subtotales);
  return [
    pdfFootLabelCell('Total', 1),
    pdfFootAmountCell(String(t.cantidadComprobantes), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
    pdfFootAmountCell(formatCurrency(t.totalImporteSinIva), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
    pdfFootAmountCell(formatCurrency(t.totalIvainscripto), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
    pdfFootAmountCell(formatCurrency(t.totalIvanoInscripto), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
    pdfFootAmountCell(formatCurrency(t.totalIvaotro), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
    pdfFootAmountCell(formatCurrency(t.totalExento), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
    pdfFootAmountCell(formatCurrency(t.totalImporteTotal), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS)
  ];
};

const totalesDesdeSubtotales = (subtotales: LibroIvaVentasSubtotalProvincia[]): LibroIvaVentasTotales => {
  const t = sumarSubtotalesProvincia(subtotales);
  return {
    netoGravado: t.totalImporteSinIva,
    iva21: t.totalIvainscripto,
    iva105: t.totalIvanoInscripto,
    iva27: t.totalIvaotro,
    exento: t.totalExento,
    total: t.totalImporteTotal
  };
};

const totalesDesdeFacturas = (facturas: FacturaVenta[]): LibroIvaVentasTotales =>
  facturas.reduce(
    (acc, f) => ({
      netoGravado: acc.netoGravado + (f.importeSinIva ?? 0),
      iva21: acc.iva21 + (f.ivainscripto ?? 0),
      iva105: acc.iva105 + (f.ivanoInscripto ?? 0),
      iva27: acc.iva27 + (f.ivaotro ?? 0),
      exento: acc.exento + (f.exento ?? 0),
      total: acc.total + (f.importeTotal ?? 0)
    }),
    { ...totalesVentasInicial }
  );

type LibroIvaVentasCompleto = {
  facturas: FacturaVenta[];
  subtotalesPorProvincia: LibroIvaVentasSubtotalProvincia[];
};

type LibroIvaVentasFiltros = {
  fechaDesde: string;
  fechaHasta: string;
  nroCliente: string;
  nroSuc: string;
  nombreCliente: string;
  idCompania: string;
};

const filtrosToParams = (filters: LibroIvaVentasFiltros) => ({
  fechaDesde: filters.fechaDesde || undefined,
  fechaHasta: filters.fechaHasta || undefined,
  nroCliente: filters.nroCliente ? parseInt(filters.nroCliente) : undefined,
  nroSuc: filters.nroSuc ? parseInt(filters.nroSuc) : undefined,
  nombreCliente: filters.nombreCliente || undefined,
  idCompania: filters.idCompania || undefined,
});

const fetchLibroIvaVentasCompleto = async (filters: LibroIvaVentasFiltros): Promise<LibroIvaVentasCompleto> => {
  const todasLasFacturas: FacturaVenta[] = [];
  let subtotalesApi: LibroIvaVentasSubtotalProvincia[] | undefined;
  const pageSize = 1000;
  let pageNumber = 1;
  let totalPages = 1;

  do {
    const response = await facturaVentaService.getLibroIvaVentas({
      pageNumber,
      pageSize,
      ...filtrosToParams(filters),
    });

    todasLasFacturas.push(...response.items);
    if (pageNumber === 1) subtotalesApi = response.subtotalesPorProvincia;
    totalPages = response.totalPages;
    pageNumber++;
  } while (pageNumber <= totalPages);

  return {
    facturas: todasLasFacturas,
    subtotalesPorProvincia: subtotalesApi
      ? ordenarSubtotalesProvincia(subtotalesApi)
      : agruparSubtotalesPorProvincia(todasLasFacturas)
  };
};

/** Aplica el filtro de provincias y la provincia elegida en el resumen (null = todas las del filtro). */
const aplicarFiltroProvincias = (
  libro: LibroIvaVentasCompleto,
  filtro: string[],
  seleccionada: string | null
): LibroIvaVentasCompleto & { listado: FacturaVenta[] } => {
  const facturas = libro.facturas.filter((f) => provinciaEnFiltro(f.codigoProvincia, filtro));
  return {
    facturas,
    subtotalesPorProvincia: libro.subtotalesPorProvincia.filter((s) =>
      provinciaEnFiltro(s.codigoProvincia, filtro)
    ),
    listado: seleccionada
      ? facturas.filter((f) => claveProvincia(f.codigoProvincia) === seleccionada)
      : facturas
  };
};

const buildVentasResumenFootRowPdf = (totales: LibroIvaVentasTotales): PdfFootCellDef[] => [
  pdfFootLabelCell('Totales', 1),
  pdfFootAmountCell(formatCurrency(totales.netoGravado), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva21), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva105), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva27), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.exento), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.total), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS)
];

// Función para formatear el tipo de IVA
const formatearTipoIva = (tipoIva?: string): string => {
  if (!tipoIva) {
    return '-';
  }

  const normalized = tipoIva.toUpperCase();

  if (normalized === 'I' || normalized.includes('RESPONSABLE') || normalized.includes('INSCRIPTO')) {
    return 'Resp. Insc.';
  }

  if (normalized === 'M' || normalized.includes('MONOTRIBUTO')) {
    return 'Monotributo';
  }

  if (normalized === 'E' || normalized.includes('EXENTO')) {
    return 'Exento';
  }

  if (normalized === 'C' || normalized.includes('CONSUMIDOR') || normalized.includes('FINAL')) {
    return 'Cons. Final';
  }

  if (normalized === 'N' || normalized.includes('NO') || normalized.includes('CATEGORIZADO')) {
    return 'No Categorizado';
  }

  return tipoIva;
};

const claveLetraComprobante = (letra?: string | null): string => {
  const clave = (letra ?? '').trim().toUpperCase();
  return clave || '-';
};

const claveTipoIvaComprobante = (factura: FacturaVenta, cacheClientes: Map<number, string>): string =>
  formatearTipoIva(factura.nroCliente ? cacheClientes.get(factura.nroCliente) : undefined);

const filtrarFacturasPorLetraYTipoIva = (
  facturas: FacturaVenta[],
  letra: string | null,
  tipoIva: string | null,
  cacheClientes: Map<number, string>
): FacturaVenta[] => {
  if (!letra && !tipoIva) return facturas;
  return facturas.filter((factura) => {
    if (letra && claveLetraComprobante(factura.letra) !== letra) return false;
    if (tipoIva && claveTipoIvaComprobante(factura, cacheClientes) !== tipoIva) return false;
    return true;
  });
};

const cargarClientesParaFilas = async (
  nrosCliente: number[],
  cacheBase: Map<number, string>
): Promise<Map<number, string>> => {
  const cache = new Map(cacheBase);
  const pendientes = nrosCliente.filter((nro) => !cache.has(nro));
  if (!pendientes.length) return cache;

  await Promise.all(
    pendientes.map(async (nroCliente) => {
      try {
        const cliente = await clienteService.getCliente(nroCliente);
        if (cliente.tipoIva) {
          cache.set(nroCliente, cliente.tipoIva);
        }
      } catch (error) {
        console.error(`Error al cargar cliente ${nroCliente}:`, error);
      }
    })
  );
  return cache;
};

// Convertir facturas a filas del libro IVA
const convertToLibroIvaRows = (facturas: FacturaVenta[], cacheClientes: Map<number, string>): LibroIvaVentasRow[] => {
  return facturas.map(factura => {
    const tipoComprobante =
      (factura.tipoFormularioDescripcion ?? '').trim()
      || (factura.nroTipoFormulario ?? '').trim()
      || '-';

    const netoGravado = factura.importeSinIva ?? 0;
    const iva21 = factura.ivainscripto ?? 0;
    const iva105 = factura.ivanoInscripto ?? 0;
    const iva27 = factura.ivaotro ?? 0;
    const exento = factura.exento ?? 0;
    const total = factura.importeTotal ?? 0;

    const puntoVentaFormateado = (factura.nroSuc || 0).toString().padStart(5, '0');
    const numeroComprobanteFormateado = (factura.nroComprobante || 0).toString().padStart(8, '0');

    const fechaEmisionStr = factura.fechaEmision instanceof Date
      ? factura.fechaEmision.toISOString()
      : (factura.fechaEmision || '');

    return {
      fechaEmision: fechaEmisionStr,
      tipoComprobante,
      puntoVenta: puntoVentaFormateado,
      numeroComprobante: numeroComprobanteFormateado,
      cliente: factura.nombre || '-',
      cuit: factura.cuit || '-',
      tipoIva: formatearTipoIva(factura.nroCliente ? cacheClientes.get(factura.nroCliente) : undefined),
      letra: claveLetraComprobante(factura.letra),
      netoGravado,
      iva21,
      iva105,
      iva27,
      exento,
      total,
      nroFormulario: factura.nroFormulario
    };
  });
};

type ResumenesAbiertos = { provincia: boolean; tipoIva: boolean; letra: boolean };

type ResumenAgrupado = { porTipoIva: LibroIvaResumenGrupo[]; porLetra: LibroIvaResumenGrupo[] };

const LibroIvaVentas: React.FC = () => {
  useRegisterPageTour(LIBRO_IVA_VENTAS_TOUR);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [facturas, setFacturas] = useState<FacturaVenta[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingTxt, setExportingTxt] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewPdfMeta, setPreviewPdfMeta] = useState({
    title: 'Libro IVA Ventas',
    documentId: 'libro-iva-ventas-preview',
    downloadFileName: `libro_iva_ventas_${new Date().toISOString().slice(0, 10)}.pdf`,
  });
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;
  const [totalesInforme, setTotalesInforme] = useState<LibroIvaVentasTotales>(totalesVentasInicial);
  const [totalesListado, setTotalesListado] = useState<LibroIvaVentasTotales>(totalesVentasInicial);
  const [subtotalesProvincia, setSubtotalesProvincia] = useState<LibroIvaVentasSubtotalProvincia[] | null>(null);
  const [provinciasOpciones, setProvinciasOpciones] = useState<{ value: string; label: string }[]>([]);
  const [provinciasFiltro, setProvinciasFiltro] = useState<string[]>([]);
  const [provinciaSeleccionada, setProvinciaSeleccionada] = useState<string | null>(null);
  const [tipoIvaSeleccionado, setTipoIvaSeleccionado] = useState<string | null>(null);
  const [letraSeleccionada, setLetraSeleccionada] = useState<string | null>(null);
  const libroCompletoCacheRef = useRef<{ key: string; promise: Promise<LibroIvaVentasCompleto> } | null>(null);
  const [loadingIvaSimple, setLoadingIvaSimple] = useState(false);
  const [clientesCache, setClientesCache] = useState<Map<number, string>>(new Map());
  const clientesCacheRef = useRef(clientesCache);
  clientesCacheRef.current = clientesCache;
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [resumenesAbiertos, setResumenesAbiertos] = useState<ResumenesAbiertos>({
    provincia: true,
    tipoIva: true,
    letra: true
  });
  const [resumenAgrupado, setResumenAgrupado] = useState<ResumenAgrupado | null>(null);
  const [loadingResumenAgrupado, setLoadingResumenAgrupado] = useState(false);

  // Inicializar fechas: primer día del mes actual y último día del mes actual
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [filters, setFilters] = useState<LibroIvaVentasFiltros>({
    fechaDesde: firstDayOfMonth.toISOString().split('T')[0],
    fechaHasta: lastDayOfMonth.toISOString().split('T')[0],
    nroCliente: '',
    nroSuc: '',
    nombreCliente: '',
    idCompania: readGlobalIdCompania() ?? '',
  });

  const resolveCompaniaParaInforme = useCallback(async (): Promise<Compania | null> => {
    const id = filters.idCompania?.trim();
    if (id) {
      try {
        return await companiaService.getCompania(id);
      } catch {
        return null;
      }
    }
    return fetchCompaniaParaPdf();
  }, [filters.idCompania]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await companiaService.getCompanias({ pageNumber: 1, pageSize: 200 });
        if (!cancelled) {
          setCompanias(
            (list || []).map((c) => ({
              ...c,
              idCompania: String(c.idCompania || '').trim(),
            })),
          );
        }
      } catch {
        if (!cancelled) setCompanias([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const provincias: Provincia[] = [];
        let pageNumber = 1;
        let totalPages = 1;
        do {
          const res = await provinciaService.getProvincias({ pageNumber, pageSize: 100 });
          provincias.push(...(res.items || []));
          totalPages = res.totalPages || 1;
          pageNumber++;
        } while (pageNumber <= totalPages && !cancelled);
        if (cancelled) return;
        const opciones = provincias
          .filter((p) => (p.nombreProvincia ?? '').trim())
          .map((p) => ({
            value: claveProvincia(p.codigoProvincia),
            label: (p.nombreProvincia ?? '').trim(),
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'es'));
        setProvinciasOpciones([...opciones, { value: SIN_PROVINCIA_KEY, label: SIN_PROVINCIA_LABEL }]);
      } catch {
        if (!cancelled) setProvinciasOpciones([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const obtenerLibroCompletoCacheado = useCallback((): Promise<LibroIvaVentasCompleto> => {
    const key = JSON.stringify(filters);
    if (libroCompletoCacheRef.current?.key !== key) {
      const promise = fetchLibroIvaVentasCompleto(filters);
      promise.catch(() => {
        if (libroCompletoCacheRef.current?.promise === promise) libroCompletoCacheRef.current = null;
      });
      libroCompletoCacheRef.current = { key, promise };
    }
    return libroCompletoCacheRef.current.promise;
  }, [filters]);

  const filtroListadoActivo =
    provinciasFiltro.length > 0 ||
    provinciaSeleccionada !== null ||
    tipoIvaSeleccionado !== null ||
    letraSeleccionada !== null;

  const aplicarSeleccionLetraYTipoIva = useCallback(async (
    facturasBase: FacturaVenta[]
  ): Promise<{ facturas: FacturaVenta[]; cache: Map<number, string> }> => {
    let cache = clientesCacheRef.current;
    if (!letraSeleccionada && !tipoIvaSeleccionado) return { facturas: facturasBase, cache };
    if (tipoIvaSeleccionado) {
      const nrosCliente = Array.from(
        new Set(facturasBase.map((f) => f.nroCliente).filter((nro): nro is number => !!nro))
      );
      cache = await cargarClientesParaFilas(nrosCliente, cache);
      clientesCacheRef.current = cache;
      setClientesCache(cache);
    }
    return {
      facturas: filtrarFacturasPorLetraYTipoIva(facturasBase, letraSeleccionada, tipoIvaSeleccionado, cache),
      cache
    };
  }, [letraSeleccionada, tipoIvaSeleccionado]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      let items: FacturaVenta[];
      let cacheParaFilas = clientesCacheRef.current;

      if (filtroListadoActivo) {
        const filtrado = aplicarFiltroProvincias(
          await obtenerLibroCompletoCacheado(),
          provinciasFiltro,
          provinciaSeleccionada
        );
        const seleccion = await aplicarSeleccionLetraYTipoIva(filtrado.listado);
        const listado = seleccion.facturas;
        cacheParaFilas = seleccion.cache;
        items = listado.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
        setTotalCount(listado.length);
        setTotalesInforme(totalesDesdeSubtotales(filtrado.subtotalesPorProvincia));
        setTotalesListado(totalesDesdeFacturas(listado));
        setSubtotalesProvincia(filtrado.subtotalesPorProvincia);
      } else {
        const response = await facturaVentaService.getLibroIvaVentas({
          pageNumber: page + 1,
          pageSize: rowsPerPage,
          ...filtrosToParams(filters),
        });
        items = response.items;
        const totales: LibroIvaVentasTotales = {
          netoGravado: response.totalImporteSinIva ?? 0,
          iva21: response.totalIvainscripto ?? 0,
          iva105: response.totalIvanoInscripto ?? 0,
          iva27: response.totalIvaotro ?? 0,
          exento: response.totalExento ?? 0,
          total: response.totalImporteTotal ?? 0
        };
        setTotalCount(response.totalCount);
        setTotalesInforme(totales);
        setTotalesListado(totales);
        setSubtotalesProvincia(
          response.subtotalesPorProvincia
            ? ordenarSubtotalesProvincia(response.subtotalesPorProvincia)
            : null
        );
      }

      setFacturas(items);

      // Cargar tipos de IVA de los clientes únicos
      const clientesUnicos = new Set<number>();
      items.forEach(factura => {
        if (factura.nroCliente) {
          clientesUnicos.add(factura.nroCliente);
        }
      });

      // Cargar los tipos de IVA de los clientes que no están en el cache
      const cacheActualizado = new Map(cacheParaFilas);
      const nuevosClientes = Array.from(clientesUnicos).filter(nro => !cacheParaFilas.has(nro));
      if (nuevosClientes.length > 0) {
        await Promise.all(
          nuevosClientes.map(async (nroCliente) => {
            try {
              const cliente = await clienteService.getCliente(nroCliente);
              if (cliente.tipoIva) {
                cacheActualizado.set(nroCliente, cliente.tipoIva);
              }
            } catch (error) {
              console.error(`Error al cargar cliente ${nroCliente}:`, error);
            }
          })
        );
        // Actualizar el cache del estado
        clientesCacheRef.current = cacheActualizado;
        setClientesCache(cacheActualizado);
      }
    } catch (error) {
      console.error('Error al obtener el libro IVA ventas:', error);
      enqueueSnackbar('No se pudieron cargar los datos del libro IVA.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    enqueueSnackbar,
    page,
    rowsPerPage,
    filters,
    filtroListadoActivo,
    provinciasFiltro,
    provinciaSeleccionada,
    aplicarSeleccionLetraYTipoIva,
    obtenerLibroCompletoCacheado
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const necesitaResumenAgrupado = resumenesAbiertos.tipoIva || resumenesAbiertos.letra;

  useEffect(() => {
    if (!necesitaResumenAgrupado) return;
    let cancelled = false;
    setLoadingResumenAgrupado(true);
    (async () => {
      try {
        const { listado } = aplicarFiltroProvincias(
          await obtenerLibroCompletoCacheado(),
          provinciasFiltro,
          provinciaSeleccionada
        );
        const nrosCliente = Array.from(
          new Set(listado.map((f) => f.nroCliente).filter((nro): nro is number => !!nro))
        );
        const cache = await cargarClientesParaFilas(nrosCliente, clientesCacheRef.current);
        if (cancelled) return;
        setClientesCache(cache);
        const rows = convertToLibroIvaRows(listado, cache);
        setResumenAgrupado({ porTipoIva: resumenPorTipoIva(rows), porLetra: resumenPorLetra(rows) });
      } catch (error) {
        console.error('Error al calcular resúmenes del libro IVA ventas:', error);
        if (!cancelled) {
          setResumenAgrupado(null);
          enqueueSnackbar('No se pudieron calcular los resúmenes por Tipo IVA y Letra.', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingResumenAgrupado(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [necesitaResumenAgrupado, obtenerLibroCompletoCacheado, provinciasFiltro, provinciaSeleccionada, enqueueSnackbar]);

  const toggleResumen = (clave: keyof ResumenesAbiertos) => (expanded: boolean) =>
    setResumenesAbiertos((prev) => ({ ...prev, [clave]: expanded }));

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const handleClosePdfPreview = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const name = event.target.name || '';
    const value = event.target.value;
    setFilters({ ...filters, [name]: value });
    setProvinciaSeleccionada(null);
    setTipoIvaSeleccionado(null);
    setLetraSeleccionada(null);
    setPage(0);
  };

  const handleProvinciasFiltroChange = (value: string | string[]) => {
    const seleccion = typeof value === 'string' ? value.split(',') : value;
    setProvinciasFiltro(seleccion);
    setProvinciaSeleccionada((prev) => (prev && seleccion.length > 0 && !seleccion.includes(prev) ? null : prev));
    setPage(0);
  };

  const nombreProvinciaPorClave = (clave: string): string =>
    provinciasOpciones.find((o) => o.value === clave)?.label
    ?? subtotalesProvincia?.find((s) => claveProvincia(s.codigoProvincia) === clave)?.nombreProvincia
    ?? clave;

  const descripcionFiltroProvincias = (): string => {
    const provincias = provinciasFiltro.length
      ? provinciasFiltro.map(nombreProvinciaPorClave).join(', ')
      : 'Todas';
    const detalle = provinciaSeleccionada
      ? ` — Detalle de comprobantes: ${nombreProvinciaPorClave(provinciaSeleccionada)}`
      : '';
    const extras = [
      tipoIvaSeleccionado ? `Tipo IVA: ${tipoIvaSeleccionado}` : '',
      letraSeleccionada ? `Letra: ${letraSeleccionada}` : ''
    ].filter(Boolean);
    const detalleExtras = extras.length ? ` — ${extras.join(' — ')}` : '';
    return `Provincias: ${provincias}${detalle}${detalleExtras}`;
  };

  const handleToggleProvinciaSeleccionada = (clave: string) => {
    setProvinciaSeleccionada((prev) => (prev === clave ? null : clave));
    setPage(0);
  };

  const handleToggleTipoIvaSeleccionado = (clave: string) => {
    setTipoIvaSeleccionado((prev) => (prev === clave ? null : clave));
    setPage(0);
  };

  const handleToggleLetraSeleccionada = (clave: string) => {
    setLetraSeleccionada((prev) => (prev === clave ? null : clave));
    setPage(0);
  };

  const obtenerTodasLasCompras = async (): Promise<LibroIvaComprasItem[]> => {
    const todasLasCompras: LibroIvaComprasItem[] = [];
    const pageSize = 20;
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const response = await facturaCompraService.getLibroIvaCompras({
        pageNumber,
        pageSize,
        fechaDesde: filters.fechaDesde || undefined,
        fechaHasta: filters.fechaHasta || undefined,
        idCompania: filters.idCompania || undefined,
      });
      todasLasCompras.push(...response.items);
      totalPages = response.totalPages;
      pageNumber++;
    } while (pageNumber <= totalPages);

    return todasLasCompras;
  };

  // Función auxiliar para obtener todas las páginas de resultados
  const obtenerTodasLasPaginas = async (): Promise<FacturaVenta[]> =>
    (await obtenerLibroCompletoCacheado()).facturas;

  /** Libro completo con el filtro de provincias y la provincia elegida en el resumen aplicados. */
  const obtenerLibroCompleto = async (): Promise<LibroIvaVentasCompleto> => {
    const filtrado = aplicarFiltroProvincias(
      await obtenerLibroCompletoCacheado(),
      provinciasFiltro,
      provinciaSeleccionada
    );
    const { facturas: facturasListado } = await aplicarSeleccionLetraYTipoIva(filtrado.listado);
    return { facturas: facturasListado, subtotalesPorProvincia: filtrado.subtotalesPorProvincia };
  };

  const handleIvaSimple = async () => {
    try {
      setLoadingIvaSimple(true);

      const [todasLasFacturas, todasLasCompras, compania] = await Promise.all([
        obtenerTodasLasPaginas(),
        obtenerTodasLasCompras(),
        resolveCompaniaParaInforme(),
      ]);

      if (!todasLasFacturas.length && !todasLasCompras.length) {
        enqueueSnackbar('No hay datos para el informe IVA Simple en el período seleccionado.', { variant: 'warning' });
        return;
      }

      const clientesUnicos = new Set<number>();
      todasLasFacturas.forEach((factura) => {
        if (factura.nroCliente) clientesUnicos.add(factura.nroCliente);
      });
      const cacheIvaSimple = await cargarClientesParaFilas(Array.from(clientesUnicos), clientesCache);
      setClientesCache(cacheIvaSimple);

      const filasVentas = convertToLibroIvaRows(todasLasFacturas, cacheIvaSimple);
      const filasCompras = todasLasCompras.map((item) => {
        const tipoComprobante =
          (item.documento ?? '').trim() || (item.nroTipoFormulario ?? '').trim() || '-';
        return {
          tipoComprobante,
          tipoIva: formatearTipoIva(item.tipoIva),
          netoGravado: item.netoGrav ?? 0,
          iva21: item.iva21 ?? 0,
          iva105: item.iva10 ?? 0,
          iva27: item.iva27 ?? 0,
          exento: item.exento ?? 0,
          total: item.importeTotal ?? 0,
        };
      });

      const informe = buildInformeIvaSimple(filasVentas, filasCompras);
      const emitidoEn = new Date();
      const logoPng = compania?.idCompania
        ? await fetchLogoPngDataUrl(compania.idCompania)
        : null;

      const pdfBlob = await generarPdfIvaSimple({
        informe,
        compania,
        fechaDesde: filters.fechaDesde,
        fechaHasta: filters.fechaHasta,
        emitidoEn,
        logoPng,
      });

      const url = URL.createObjectURL(pdfBlob);
      setPreviewPdfMeta({
        title: 'IVA Simple',
        documentId: 'iva-simple-preview',
        downloadFileName: `iva_simple_${filters.fechaDesde}_${filters.fechaHasta}.pdf`,
      });
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('PDF IVA Simple generado.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar informe IVA Simple:', error);
      enqueueSnackbar('No se pudo generar el informe IVA Simple.', { variant: 'error' });
    } finally {
      setLoadingIvaSimple(false);
    }
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExporting(true);
      // Obtener todos los registros para la exportación (todas las páginas)
      const { facturas: todasLasFacturas, subtotalesPorProvincia } = await obtenerLibroCompleto();

      if (!todasLasFacturas.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      // Cargar tipos de IVA de los clientes únicos para la exportación
      const clientesUnicosExport = new Set<number>();
      todasLasFacturas.forEach(factura => {
        if (factura.nroCliente) {
          clientesUnicosExport.add(factura.nroCliente);
        }
      });

      const cacheExport = new Map(clientesCache);
      const nuevosClientesExport = Array.from(clientesUnicosExport).filter(nro => !clientesCache.has(nro));
      if (nuevosClientesExport.length > 0) {
        await Promise.all(
          nuevosClientesExport.map(async (nroCliente) => {
            try {
              const cliente = await clienteService.getCliente(nroCliente);
              if (cliente.tipoIva) {
                cacheExport.set(nroCliente, cliente.tipoIva);
              }
            } catch (error) {
              console.error(`Error al cargar cliente ${nroCliente}:`, error);
            }
          })
        );
      }

      const rows = convertToLibroIvaRows(todasLasFacturas, cacheExport);
      const totalesExport = sumLibroIvaVentasRows(rows);

      const data = rows.map((row) => ({
        'Fecha': row.fechaEmision ? formatearFecha(row.fechaEmision) : '',
        'Tipo': row.tipoComprobante,
        'Punto Venta': row.puntoVenta,
        'Número': row.numeroComprobante,
        'Cliente': row.cliente,
        'CUIT': row.cuit,
        'Tipo IVA': row.tipoIva,
        'Neto Gravado': row.netoGravado,
        'IVA 21%': row.iva21,
        'IVA 10.5%': row.iva105,
        'IVA 27%': row.iva27,
        'Exento': row.exento,
        'Total': row.total
      }));

      data.push({
        'Fecha': 'Totales',
        'Tipo': '',
        'Punto Venta': '',
        'Número': '',
        'Cliente': '',
        'CUIT': '',
        'Tipo IVA': '',
        'Neto Gravado': totalesExport.netoGravado,
        'IVA 21%': totalesExport.iva21,
        'IVA 10.5%': totalesExport.iva105,
        'IVA 27%': totalesExport.iva27,
        'Exento': totalesExport.exento,
        'Total': totalesExport.total
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Libro IVA Ventas');

      const totalesProvincia = sumarSubtotalesProvincia(subtotalesPorProvincia);
      const dataProvincia = [
        ...subtotalesPorProvincia.map((s) => ({
          'Provincia': nombreProvinciaSubtotal(s),
          'Comprobantes': s.cantidadComprobantes ?? 0,
          'Neto Gravado': s.totalImporteSinIva ?? 0,
          'IVA 1 (21%)': s.totalIvainscripto ?? 0,
          'IVA 2 (10.5%)': s.totalIvanoInscripto ?? 0,
          'IVA 27%': s.totalIvaotro ?? 0,
          'Exento': s.totalExento ?? 0,
          'Total': s.totalImporteTotal ?? 0
        })),
        {
          'Provincia': 'Total',
          'Comprobantes': totalesProvincia.cantidadComprobantes,
          'Neto Gravado': totalesProvincia.totalImporteSinIva,
          'IVA 1 (21%)': totalesProvincia.totalIvainscripto,
          'IVA 2 (10.5%)': totalesProvincia.totalIvanoInscripto,
          'IVA 27%': totalesProvincia.totalIvaotro,
          'Exento': totalesProvincia.totalExento,
          'Total': totalesProvincia.totalImporteTotal
        }
      ];
      const worksheetProvincia = XLSX.utils.json_to_sheet(dataProvincia);
      XLSX.utils.sheet_add_aoa(worksheetProvincia, [[], [descripcionFiltroProvincias()]], { origin: -1 });
      XLSX.utils.book_append_sheet(workbook, worksheetProvincia, 'Por provincia');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `libro_iva_ventas_${today}.xlsx`);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const parseInputDate = (value: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const downloadTextFile = (content: string, fileName: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportTxtArca = async () => {
    const fechaDesde = parseInputDate(filters.fechaDesde);
    const fechaHasta = parseInputDate(filters.fechaHasta);

    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Debe seleccionar fecha desde y fecha hasta para exportar TXT.', { variant: 'warning' });
      return;
    }

    if (fechaDesde > fechaHasta) {
      enqueueSnackbar('La fecha desde no puede ser mayor que la fecha hasta.', { variant: 'warning' });
      return;
    }

    try {
      setExportingTxt(true);
      const result = await facturaVentaService.exportLibroIvaVentasArcaTxt({
        fechaDesde,
        fechaHasta,
        idCompania: filters.idCompania || undefined,
      });

      downloadTextFile(result.comprobantesContent, result.comprobantesFileName);
      downloadTextFile(result.alicuotasContent, result.alicuotasFileName);
      enqueueSnackbar('Archivos TXT generados para ARCA.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar TXT ARCA:', error);
      enqueueSnackbar('No se pudieron generar los archivos TXT para ARCA.', { variant: 'error' });
    } finally {
      setExportingTxt(false);
    }
  };

  const handleExportPdf = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay información para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      // Obtener todos los registros para la exportación PDF (todas las páginas)
      const { facturas: todasLasFacturas, subtotalesPorProvincia } = await obtenerLibroCompleto();

      if (!todasLasFacturas.length) {
        enqueueSnackbar('No hay información para exportar.', { variant: 'warning' });
        return;
      }

      // Cargar tipos de IVA de los clientes únicos para la exportación PDF
      const clientesUnicosPdf = new Set<number>();
      todasLasFacturas.forEach(factura => {
        if (factura.nroCliente) {
          clientesUnicosPdf.add(factura.nroCliente);
        }
      });

      const cachePdf = new Map(clientesCache);
      const nuevosClientesPdf = Array.from(clientesUnicosPdf).filter(nro => !clientesCache.has(nro));
      if (nuevosClientesPdf.length > 0) {
        await Promise.all(
          nuevosClientesPdf.map(async (nroCliente) => {
            try {
              const cliente = await clienteService.getCliente(nroCliente);
              if (cliente.tipoIva) {
                cachePdf.set(nroCliente, cliente.tipoIva);
              }
            } catch (error) {
              console.error(`Error al cargar cliente ${nroCliente}:`, error);
            }
          })
        );
      }

      const rows = convertToLibroIvaRows(todasLasFacturas, cachePdf);
      const totalesExport = sumLibroIvaVentasRows(rows);

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const pageW = doc.internal.pageSize.getWidth();
      const headerTop = 32;
      const logoMaxW = 76;
      const logoMaxH = 42;
      const rightColW = 260;
      const rightX = pageW - marginX;

      const companiaSeleccionada = Boolean(filters.idCompania?.trim());
      const compania = await resolveCompaniaParaInforme();
      const logoPng =
        companiaSeleccionada && compania?.idCompania
          ? await fetchLogoPngDataUrl(compania.idCompania)
          : null;

      if (logoPng) {
        doc.addImage(logoPng, 'PNG', marginX, headerTop, logoMaxW, logoMaxH, undefined, 'FAST');
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(25, 55, 95);
      doc.text('Libro IVA Ventas', pageW / 2, headerTop + 26, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(7);
      const datosComp =
        companiaSeleccionada && compania ? lineasDatosCompania(compania) : [];
      let ry = headerTop + 4;
      datosComp.forEach((linea) => {
        const wrapped = doc.splitTextToSize(linea, rightColW);
        wrapped.forEach((sub: string) => {
          doc.text(sub, rightX, ry, { align: 'right' });
          ry += 8;
        });
      });

      const headerBottom = Math.max(
        logoPng ? headerTop + logoMaxH + 4 : headerTop,
        ry + 4,
        headerTop + 48,
      );
      let currentY = headerBottom;

      doc.setFontSize(8);
      const generadoEn = new Date();
      doc.text(`Generado: ${generadoEn.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 12;

      if (filters.fechaDesde && filters.fechaHasta) {
        const fechaDesdeParts = filters.fechaDesde.split('-');
        const fechaHastaParts = filters.fechaHasta.split('-');
        const fechaDesdeLocal = new Date(
          parseInt(fechaDesdeParts[0], 10),
          parseInt(fechaDesdeParts[1], 10) - 1,
          parseInt(fechaDesdeParts[2], 10)
        );
        const fechaHastaLocal = new Date(
          parseInt(fechaHastaParts[0], 10),
          parseInt(fechaHastaParts[1], 10) - 1,
          parseInt(fechaHastaParts[2], 10)
        );
        doc.text(
          `Período: ${formatearFecha(fechaDesdeLocal)} - ${formatearFecha(fechaHastaLocal)}`,
          marginX,
          currentY
        );
        currentY += 14;
      }

      const autoTable = (await import('jspdf-autotable')).default;
      const tableWidth = pdfLibroIvaTableWidth(pageW, marginX);

      autoTable(doc, {
        startY: currentY,
        tableWidth,
        head: [pdfHeadRowRightAligned(['Fecha', 'Tipo', 'P.Vta', 'Número', 'Cliente', 'CUIT', 'Tipo IVA', 'Neto Grav.', 'IVA 21%', 'IVA 10.5%', 'IVA 27%', 'Exento', 'Total'], 7)],
        body: rows.map((row) => [
          row.fechaEmision ? formatearFecha(row.fechaEmision) : '-',
          row.tipoComprobante,
          row.puntoVenta,
          row.numeroComprobante,
          row.cliente,
          row.cuit,
          row.tipoIva,
          formatCurrency(row.netoGravado),
          formatCurrency(row.iva21),
          formatCurrency(row.iva105),
          formatCurrency(row.iva27),
          formatCurrency(row.exento),
          formatCurrency(row.total)
        ]),
        foot: [buildVentasFootRowPdf(totalesExport)],
        showFoot: 'lastPage',
        margin: { left: marginX, right: marginX },
        styles: {
          fontSize: 5.5,
          cellPadding: 1,
          overflow: 'ellipsize',
          valign: 'middle',
          minCellHeight: 9
        },
        headStyles: { fillColor: [245, 158, 11], textColor: 255, fontSize: 6, cellPadding: 1.2 },
        bodyStyles: { fontSize: 5.5 },
        footStyles: pdfLibroIvaFootStyles(PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
        columnStyles: pdfLibroIvaVentasColumnStyles(tableWidth)
      });

      const docConTabla = doc as typeof doc & { lastAutoTable?: { finalY: number } };
      const pageH = doc.internal.pageSize.getHeight();
      const tablaResumenStyles = {
        styles: {
          fontSize: 5.5,
          cellPadding: 1,
          overflow: 'linebreak' as const,
          valign: 'middle' as const,
          minCellHeight: 9
        },
        headStyles: { fillColor: [245, 158, 11] as [number, number, number], textColor: 255, fontSize: 6, cellPadding: 1.2 },
        bodyStyles: { fontSize: 5.5 },
        footStyles: pdfLibroIvaFootStyles(PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS)
      };
      const escribirTituloSeccion = (titulo: string, espacioMinimo: number): number => {
        let y = (docConTabla.lastAutoTable?.finalY ?? currentY) + 22;
        if (y > pageH - espacioMinimo) {
          doc.addPage();
          y = 48;
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(25, 55, 95);
        doc.text(titulo, marginX, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        return y + 12;
      };

      const resumenPorTipoIva = buildVentasResumenPorTipoIvaBody(rows);
      if (resumenPorTipoIva.length > 0) {
        autoTable(doc, {
          ...tablaResumenStyles,
          startY: escribirTituloSeccion('Resumen por Tipo IVA', 72),
          tableWidth,
          head: [pdfHeadRowRightAligned(PDF_LIBRO_IVA_VENTAS_RESUMEN_HEAD, 1)],
          body: resumenPorTipoIva,
          foot: [buildVentasResumenFootRowPdf(totalesExport)],
          showFoot: 'lastPage',
          margin: { left: marginX, right: marginX },
          columnStyles: pdfLibroIvaVentasResumenColumnStyles(tableWidth)
        });
      }

      const resumenPorLetra = buildVentasResumenPorLetraBody(rows);
      if (resumenPorLetra.length > 0) {
        autoTable(doc, {
          ...tablaResumenStyles,
          startY: escribirTituloSeccion('Resumen por Letra', 72),
          tableWidth,
          head: [pdfHeadRowRightAligned(['Letra', ...PDF_LIBRO_IVA_VENTAS_RESUMEN_HEAD.slice(1)], 1)],
          body: resumenPorLetra,
          foot: [buildVentasResumenFootRowPdf(totalesExport)],
          showFoot: 'lastPage',
          margin: { left: marginX, right: marginX },
          columnStyles: pdfLibroIvaVentasResumenColumnStyles(tableWidth)
        });
      }

      let ivaY = (docConTabla.lastAutoTable?.finalY ?? currentY) + 18;
      if (ivaY > pageH - 40) {
        doc.addPage();
        ivaY = 48;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(
        `IVA 1 (21%): ${formatCurrency(totalesExport.iva21)}     IVA 2 (10.5%): ${formatCurrency(totalesExport.iva105)}`,
        marginX,
        ivaY
      );
      doc.setFont('helvetica', 'normal');

      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(25, 55, 95);
      doc.text('Subtotales por Provincia', marginX, 48);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      autoTable(doc, {
        ...tablaResumenStyles,
        startY: 60,
        tableWidth,
        head: [pdfHeadRowRightAligned(PDF_LIBRO_IVA_VENTAS_PROVINCIA_HEAD, 1)],
        body: buildVentasProvinciaBody(subtotalesPorProvincia),
        foot: [buildVentasProvinciaFootRowPdf(subtotalesPorProvincia)],
        showFoot: 'lastPage',
        margin: { left: marginX, right: marginX },
        columnStyles: pdfLibroIvaVentasProvinciaColumnStyles(tableWidth)
      });
      doc.setFontSize(8);
      doc.text(
        doc.splitTextToSize(descripcionFiltroProvincias(), tableWidth),
        marginX,
        (docConTabla.lastAutoTable?.finalY ?? 60) + 16
      );

      const pdfBlob = doc.output('blob');
      const safeBlob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfMeta({
        title: 'Libro IVA Ventas',
        documentId: 'libro-iva-ventas-preview',
        downloadFileName: `libro_iva_ventas_${new Date().toISOString().slice(0, 10)}.pdf`,
      });
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        enqueueSnackbar('Exportación cancelada por el usuario.', { variant: 'warning' });
      } else {
        console.error('Error al generar el PDF:', error);
        enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const renderFooter = () => {
    if (loading || facturas.length === 0) {
      return null;
    }

    return (
      <TableRow data-tour-id="informe-totales">
        <TableCell colSpan={7} sx={footerCellSx}>
          Totales
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesListado.netoGravado)}
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesListado.iva21)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesListado.iva105)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesListado.iva27)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesListado.exento)}
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesListado.total)}
        </TableCell>
      </TableRow>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={LIBRO_IVA_VENTAS_TABLE_COL_COUNT} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!loading && facturas.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={LIBRO_IVA_VENTAS_TABLE_COL_COUNT} align="center">
            No hay facturas para el período seleccionado.
          </TableCell>
        </TableRow>
      );
    }

    const rows = convertToLibroIvaRows(facturas, clientesCache);
    
    // Debug: verificar cuántos registros se están mostrando
    console.log('Registros a mostrar:', rows.length, 'de facturas:', facturas.length, 'totalCount:', totalCount);

    return (
      <>
        {rows.map((row, index) => {
          // Usar index como parte de la key para evitar problemas con duplicados
          return (
            <TableRow key={`${row.nroFormulario}-${index}`} hover>
              <TableCell>
                {row.fechaEmision ? formatearFecha(row.fechaEmision) : '-'}
              </TableCell>
              <TableCell>{row.tipoComprobante}</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {row.puntoVenta}
              </TableCell>
              <TableCell align="center">{row.numeroComprobante}</TableCell>
              <TableCell>{row.cliente}</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{row.cuit}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.tipoIva}</TableCell>
              <TableCell align="right">{formatCurrency(row.netoGravado)}</TableCell>
              <TableCell align="right">{formatCurrency(row.iva21)}</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                {formatCurrency(row.iva105)}
              </TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                {formatCurrency(row.iva27)}
              </TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                {formatCurrency(row.exento)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatCurrency(row.total)}
              </TableCell>
            </TableRow>
          );
        })}
      </>
    );
  };

  const renderCards = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (!loading && facturas.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography variant="body1" color="text.secondary">
            No hay facturas para el período seleccionado.
          </Typography>
        </Box>
      );
    }

    const rows = convertToLibroIvaRows(facturas, clientesCache);

    return (
      <Stack spacing={2}>
        {rows.map((row, index) => (
          <Card
            key={`${row.nroFormulario}-${index}`}
            sx={{
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent>
              <Stack spacing={1.5}>
                {/* Header con fecha y tipo */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {row.tipoComprobante}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.fechaEmision ? formatearFecha(row.fechaEmision) : '-'}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {formatCurrency(row.total)}
                  </Typography>
                </Stack>

                <Divider />

                {/* Información del comprobante */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      P.Vta / Número:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.puntoVenta} - {row.numeroComprobante}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Cliente:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
                      {row.cliente}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      CUIT:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.cuit}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Tipo IVA:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.tipoIva}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider />

                {/* Importes */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Neto Gravado:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(row.netoGravado)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      IVA 21%:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(row.iva21)}
                    </Typography>
                  </Stack>
                  {row.iva105 !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IVA 10.5%:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.iva105)}
                      </Typography>
                    </Stack>
                  )}
                  {row.iva27 !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IVA 27%:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.iva27)}
                      </Typography>
                    </Stack>
                  )}
                  {row.exento !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Exento:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.exento)}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  const renderMobileTotales = () => {
    if (loading || facturas.length === 0) {
      return null;
    }

    return (
      <Card sx={{ mt: 2, borderRadius: 2, border: '2px solid #bdbdbd', bgcolor: '#f0f4f8' }} data-tour-id="informe-totales">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Totales del informe
          </Typography>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Neto Gravado</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.netoGravado)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 21%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.iva21)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 10.5%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.iva105)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 27%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.iva27)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Exento</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesListado.exento)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>{formatCurrency(totalesListado.total)}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const kpisResumen: KpiBarItem[] = [
    {
      key: 'comprobantes',
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />,
      iconColor: '#757575',
      value: String(totalCount),
      label: 'comprobantes',
    },
    {
      key: 'neto',
      icon: <PriceCheckIcon sx={{ fontSize: 20 }} />,
      iconColor: '#00897b',
      value: formatCurrency(totalesInforme.netoGravado),
      valueColor: '#00897b',
      label: 'neto gravado',
    },
    {
      key: 'iva21',
      icon: <PercentIcon sx={{ fontSize: 20 }} />,
      iconColor: '#1976d2',
      value: formatCurrency(totalesInforme.iva21),
      valueColor: '#1976d2',
      label: 'IVA 1 (21%)',
    },
    {
      key: 'iva105',
      icon: <PercentIcon sx={{ fontSize: 20 }} />,
      iconColor: '#7b1fa2',
      value: formatCurrency(totalesInforme.iva105),
      valueColor: '#7b1fa2',
      label: 'IVA 2 (10.5%)',
    },
    {
      key: 'total',
      icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />,
      iconColor: '#2e7d32',
      value: formatCurrency(totalesInforme.total),
      valueColor: '#2e7d32',
      label: 'total',
    },
  ];

  const renderTablaProvincias = () => {
    if (subtotalesProvincia === null) {
      return (
        <Alert severity="info">
          El servidor no informó subtotales por provincia para esta consulta.
        </Alert>
      );
    }

    const totalesProvincia = sumarSubtotalesProvincia(subtotalesProvincia);
    const cellSx = { fontWeight: 600 };

    return (
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0', overflowX: 'auto' }}
      >
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={cellSx}>Provincia</TableCell>
              <TableCell align="right" sx={cellSx}>Comprobantes</TableCell>
              <TableCell align="right" sx={cellSx}>Neto Gravado</TableCell>
              <TableCell align="right" sx={cellSx}>IVA 1 (21%)</TableCell>
              <TableCell align="right" sx={cellSx}>IVA 2 (10.5%)</TableCell>
              <TableCell align="right" sx={cellSx}>IVA 27%</TableCell>
              <TableCell align="right" sx={cellSx}>Exento</TableCell>
              <TableCell align="right" sx={cellSx}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subtotalesProvincia.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Sin subtotales por provincia.
                </TableCell>
              </TableRow>
            ) : (
              subtotalesProvincia.map((s, index) => {
                const clave = claveProvincia(s.codigoProvincia);
                const seleccionada = provinciaSeleccionada === clave;
                return (
                  <TableRow
                    key={`${clave}-${index}`}
                    hover
                    selected={seleccionada}
                    onClick={() => handleToggleProvinciaSeleccionada(clave)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={seleccionada ? { fontWeight: 600 } : undefined}>
                      {nombreProvinciaSubtotal(s)}
                    </TableCell>
                    <TableCell align="right">{s.cantidadComprobantes ?? 0}</TableCell>
                    <TableCell align="right">{formatCurrency(s.totalImporteSinIva ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(s.totalIvainscripto ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(s.totalIvanoInscripto ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(s.totalIvaotro ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(s.totalExento ?? 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(s.totalImporteTotal ?? 0)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            {subtotalesProvincia.length > 0 ? (
              <TableRow>
                <TableCell sx={footerCellSx}>Total</TableCell>
                <TableCell align="right" sx={footerCellSx}>{totalesProvincia.cantidadComprobantes}</TableCell>
                <TableCell align="right" sx={footerCellSx}>{formatCurrency(totalesProvincia.totalImporteSinIva)}</TableCell>
                <TableCell align="right" sx={footerCellSx}>{formatCurrency(totalesProvincia.totalIvainscripto)}</TableCell>
                <TableCell align="right" sx={footerCellSx}>{formatCurrency(totalesProvincia.totalIvanoInscripto)}</TableCell>
                <TableCell align="right" sx={footerCellSx}>{formatCurrency(totalesProvincia.totalIvaotro)}</TableCell>
                <TableCell align="right" sx={footerCellSx}>{formatCurrency(totalesProvincia.totalExento)}</TableCell>
                <TableCell align="right" sx={footerCellSx}>{formatCurrency(totalesProvincia.totalImporteTotal)}</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderResumenes = () => {
    if (
      (!loading && totalCount === 0 && !provinciaSeleccionada && !tipoIvaSeleccionado && !letraSeleccionada) ||
      (loading && subtotalesProvincia === null)
    ) {
      return null;
    }

    return (
      <Box sx={{ mt: 3 }}>
        <LibroIvaResumenAccordion
          titulo="Subtotales por provincia"
          descripcion="Hacé clic en una provincia para ver solo sus comprobantes en el listado; volvé a hacer clic para ver todas."
          icon={<PlaceOutlinedIcon color="primary" fontSize="small" />}
          expanded={resumenesAbiertos.provincia}
          onChange={toggleResumen('provincia')}
          accion={
            provinciaSeleccionada ? (
              <Chip
                size="small"
                color="primary"
                label={`Listado: ${nombreProvinciaPorClave(provinciaSeleccionada)}`}
                onDelete={() => handleToggleProvinciaSeleccionada(provinciaSeleccionada)}
              />
            ) : null
          }
        >
          {renderTablaProvincias()}
        </LibroIvaResumenAccordion>

        <LibroIvaResumenAccordion
          titulo="Resumen por Tipo IVA"
          descripcion="Hacé clic en una categoría para ver solo sus comprobantes en el listado; volvé a hacer clic para ver todas."
          icon={<BadgeOutlinedIcon color="primary" fontSize="small" />}
          expanded={resumenesAbiertos.tipoIva}
          onChange={toggleResumen('tipoIva')}
          accion={
            tipoIvaSeleccionado ? (
              <Chip
                size="small"
                color="primary"
                label={`Listado: ${tipoIvaSeleccionado}`}
                onDelete={() => handleToggleTipoIvaSeleccionado(tipoIvaSeleccionado)}
              />
            ) : null
          }
        >
          <LibroIvaResumenAgrupadoTable
            columnaClave="Tipo IVA"
            grupos={resumenAgrupado?.porTipoIva ?? null}
            loading={loadingResumenAgrupado}
            claveSeleccionada={tipoIvaSeleccionado}
            onToggleClave={handleToggleTipoIvaSeleccionado}
          />
        </LibroIvaResumenAccordion>

        <LibroIvaResumenAccordion
          titulo="Resumen por Letra"
          descripcion="Hacé clic en una letra para ver solo sus comprobantes en el listado; volvé a hacer clic para ver todas."
          icon={<SortByAlphaIcon color="primary" fontSize="small" />}
          expanded={resumenesAbiertos.letra}
          onChange={toggleResumen('letra')}
          accion={
            letraSeleccionada ? (
              <Chip
                size="small"
                color="primary"
                label={`Listado: ${letraSeleccionada}`}
                onDelete={() => handleToggleLetraSeleccionada(letraSeleccionada)}
              />
            ) : null
          }
        >
          <LibroIvaResumenAgrupadoTable
            columnaClave="Letra"
            grupos={resumenAgrupado?.porLetra ?? null}
            loading={loadingResumenAgrupado}
            claveSeleccionada={letraSeleccionada}
            onToggleClave={handleToggleLetraSeleccionada}
          />
        </LibroIvaResumenAccordion>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 3 }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/ventas/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: '#1976d2'
                }}
              >
                Libro IVA Ventas
              </Typography>
            </Stack>
            <Stack 
              direction="row" 
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              data-tour-id="informe-acciones"
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SummarizeIcon />}
                onClick={() => void handleIvaSimple()}
                disabled={loading || loadingIvaSimple}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {loadingIvaSimple ? 'Generando PDF…' : 'IVA Simple'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || exporting}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                Exportar Excel
              </Button>
              <Tooltip title="Genera los archivos TXT de comprobantes y alícuotas para la presentación del Libro IVA Digital Ventas ante ARCA.">
                <Box component="span" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArticleIcon />}
                    onClick={() => void handleExportTxtArca()}
                    disabled={loading || exportingTxt}
                    sx={{
                      borderRadius: 2,
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      width: { xs: '100%', sm: 'auto' },
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {exportingTxt ? 'Generando TXT…' : 'Exportar TXT'}
                  </Button>
                </Box>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handleExportPdf()}
                disabled={loading || exportingPdf}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {exportingPdf ? 'Generando PDF…' : 'Imprimir PDF'}
              </Button>
            </Stack>
          </Stack>

          {/* Filtros */}
          <Card sx={{ mb: 3, bgcolor: '#fafafa' }} data-tour-id="informe-filtros">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Compañía"
                    allowEmpty
                    emptyOptionLabel="Todas las compañías"
                    value={filters.idCompania || null}
                    onChange={(v) => {
                      const id = v ?? '';
                      if (id) persistGlobalIdCompania(id);
                      setFilters((prev) => ({ ...prev, idCompania: id }));
                      setProvinciaSeleccionada(null);
                      setPage(0);
                    }}
                    options={companias.map((c) => ({
                      value: c.idCompania,
                      label: `${c.idCompania} - ${c.nombre}`,
                    }))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha Desde"
                    type="date"
                    name="fechaDesde"
                    value={filters.fechaDesde}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    name="fechaHasta"
                    value={filters.fechaHasta}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nro. Cliente"
                    name="nroCliente"
                    value={filters.nroCliente}
                    onChange={handleFilterChange}
                    size="small"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nro. Sucursal"
                    name="nroSuc"
                    value={filters.nroSuc}
                    onChange={handleFilterChange}
                    size="small"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nombre Cliente"
                    name="nombreCliente"
                    value={filters.nombreCliente}
                    onChange={handleFilterChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="libro-iva-provincias-label" shrink>
                      Provincias
                    </InputLabel>
                    <Select
                      labelId="libro-iva-provincias-label"
                      multiple
                      displayEmpty
                      value={provinciasFiltro}
                      onChange={(e) => handleProvinciasFiltroChange(e.target.value)}
                      input={<OutlinedInput notched label="Provincias" />}
                      renderValue={(seleccion) => {
                        if (seleccion.length === 0) return 'Todas';
                        if (seleccion.length <= 2) return seleccion.map(nombreProvinciaPorClave).join(', ');
                        return `${seleccion.length} provincias`;
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                    >
                      {provinciasOpciones.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.value} dense>
                          <Checkbox size="small" checked={provinciasFiltro.includes(opcion.value)} />
                          <ListItemText primary={opcion.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <KpisBar items={kpisResumen} ariaLabel="Resumen impositivo del período" />

          {isMobile ? (
            <Box data-tour-id="informe-resultados">
              {renderCards()}
              {renderMobileTotales()}
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  mt: 2,
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Box>
          ) : (
            /* Vista desktop: Tabla */
            <TableContainer
              component={Paper}
              data-tour-id="informe-resultados"
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTableCell-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }
              }}
            >
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 80, sm: 100 } }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 } }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'center', minWidth: { xs: 60, sm: 80 }, display: { xs: 'none', md: 'table-cell' } }}>P.Vta</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'center', minWidth: { xs: 80, sm: 100 } }}>Número</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 150, sm: 200 } }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', lg: 'table-cell' } }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', md: 'table-cell' } }}>Tipo IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 100, sm: 120 } }}>Neto Gravado</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 } }}>IVA 21%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>IVA 10.5%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>IVA 27%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>Exento</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 100, sm: 120 } }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderBody()}
                  {renderFooter()}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: { xs: 1, sm: 2 }
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              />
            </TableContainer>
          )}

          {renderResumenes()}
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title={previewPdfMeta.title}
          documentId={previewPdfMeta.documentId}
          documentType="informeCliente"
          downloadFileName={previewPdfMeta.downloadFileName}
        />
      ) : null}
    </Box>
  );
};

export default LibroIvaVentas;

